import { Suspense, lazy, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import PublicHomepage from '@/components/PublicHomepage';
import DashboardSkeleton from '@/components/DashboardSkeleton';
import { useAuth } from '@/contexts/AuthContext';
import SEO from '@/components/SEO';
const AuthenticatedDashboard = lazy(() => import('@/components/AuthenticatedDashboard'));
const AcquisitionSurvey = lazy(() => import('@/components/AcquisitionSurvey'));

const Index = () => {
  const { isPro, user, loading: authLoading, checkSubscription } = useAuth();
  // Sticky mobile CTA reveals only after the user has scrolled past the hero +
  // Live Buys terminal (both above-the-fold). Trigger = the bottom edge of
  // #live-buys-anchor crossing into the upper portion of the viewport.
  const [showStickyCta, setShowStickyCta] = useState(false);
  useEffect(() => {
    if (user) return;
    const anchor = document.getElementById('live-buys-anchor');
    if (!anchor) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Reveal when the anchor's bottom has scrolled above the viewport top
        // (i.e. user has moved past Live Buys entirely).
        const bottomAboveViewport = entry.boundingClientRect.bottom < 0;
        setShowStickyCta(bottomAboveViewport);
      },
      { threshold: 0, rootMargin: '0px' }
    );
    observer.observe(anchor);
    // Also handle the case where user lands deep on the page (anchor already past)
    const onScroll = () => {
      const rect = anchor.getBoundingClientRect();
      if (rect.bottom < 0) setShowStickyCta(true);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, [user]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', { send_to: 'AW-18075200724/m1KhCNGrzJgcENTZ9qpD', value: 9.0, currency: 'USD' });
      }
      params.delete('checkout');
      const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      // Force-refresh subscription so Pro features unlock immediately (bypasses 30s TTL).
      // Retry once after a short delay in case Stripe propagation lags the redirect.
      checkSubscription(true);
      const retry = setTimeout(() => checkSubscription(true), 2500);
      toast.success("You're now Pro! Welcome to 252signal.", {
        description: 'Your 7-day trial has started. All Pro features are unlocked.',
        duration: 6000,
      });
      return () => clearTimeout(retry);
    }
  }, [checkSubscription]);

  // ─── LOGGED-OUT VIEW ───
  // Render the public homepage immediately on the root route while auth state
  // is resolving so the hero H1 is not blocked behind session bootstrap.
  if (!user && (!authLoading || typeof window === 'undefined' || window.location.pathname === '/')) {
    return (
      <div className="min-h-screen bg-background pb-[calc(env(safe-area-inset-bottom)+5.5rem)] md:pb-0">
        <SEO title="Insider & Congress Trade Tracker | Real-Time SEC Alerts" description="Real-time SEC Form 4 and congressional trades, scored with a statistical p-value vs SPY. Verified track record. Pro from $9/month." path="/" jsonLd={{"@context":"https://schema.org","@type":"FAQPage","mainEntity":[{"@type":"Question","name":"How do I track insider trades?","acceptedAnswer":{"@type":"Answer","text":"252signal tracks every SEC Form 4 insider trade in real-time and explains each transaction in plain English."}},{"@type":"Question","name":"How do I track congress trades?","acceptedAnswer":{"@type":"Answer","text":"252signal monitors every stock trade disclosed by members of Congress under the STOCK Act, with committee conflict context."}},{"@type":"Question","name":"What is SEC Form 4?","acceptedAnswer":{"@type":"Answer","text":"SEC Form 4 is the filing required when corporate insiders buy or sell their company stock. 252signal monitors every Form 4 in real-time."}},{"@type":"Question","name":"What is the STOCK Act?","acceptedAnswer":{"@type":"Answer","text":"The STOCK Act requires senators and representatives to disclose stock trades within 45 days. 252signal tracks every disclosure."}},{"@type":"Question","name":"How much does 252signal cost?","acceptedAnswer":{"@type":"Answer","text":"252signal Pro starts at $9/month with cancel anytime. A free tier is available."}}]}} />
        <Navbar />
        <PublicHomepage />

        {/* Mobile sticky CTA bar — reveals only after the user scrolls past the
            Live Buys terminal so it doesn't crowd above-the-fold content.
            Respects iOS safe-area inset and animates in from below. */}
        <div
          className={`fixed bottom-0 left-0 right-0 z-40 border-t border-border/70 bg-background/95 px-4 pt-3 backdrop-blur transition-transform duration-300 md:hidden ${
            showStickyCta ? 'translate-y-0' : 'translate-y-full'
          }`}
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
          aria-hidden={!showStickyCta}
        >
          <div className="flex items-center justify-between gap-3">
            <Link
              to="/signup"
              className="inline-flex h-11 min-h-[44px] flex-1 items-center justify-center rounded-md bg-primary text-sm font-semibold text-primary-foreground active:opacity-80"
            >
              Start 7-day trial
            </Link>
            <Link
              to="/login"
              className="inline-flex min-h-[44px] items-center justify-center px-2 text-sm font-medium text-muted-foreground underline-offset-2 hover:underline active:opacity-80"
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ─── LOGGED-IN VIEW ───
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <Suspense fallback={<DashboardSkeleton bodyOnly />}>
        <AuthenticatedDashboard />
      </Suspense>
      <Suspense fallback={null}>
        <AcquisitionSurvey />
      </Suspense>
    </div>
  );
};

export default Index;
